import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { logger } from "@/modules/logger";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { buildProactivePaywallEventPayload } from "@/domains/metrics/monetization";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { useAppStore } from "@/store/utils/hooks";
import { UpsellModal } from "@/components/modal/upsell";
import { isCommercialRolloutActive } from "@/modules/commercial-plans";

import { ProactiveUpsellModalView } from "./proactive-upsell-modal-view";
import { startProactiveCheckout } from "./proactive-checkout";

const REMINDER_SHILL_PROACTIVE_PAYWALL_TITLE =
  "Upgrade to Proactive to get reminders via Mem agent";
const REMINDER_SHILL_PROACTIVE_TRIAL_TITLE =
  "Try Proactive free for 7 days to get reminders via Mem agent";

interface ProactiveUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  checkoutResetKey: number;
  checkoutIsExternallyPending?: boolean;
}

export const ProactiveUpsellModal = observer(
  ({
    isOpen,
    onClose,
    checkoutResetKey,
    checkoutIsExternallyPending = false,
  }: ProactiveUpsellModalProps) => {
    const { store } = useAppStore();
    const location = useLocation();
    const [isSubscribing, setIsSubscribing] = useState(false);
    const checkoutIsPending = isSubscribing || checkoutIsExternallyPending;
    const proactivePricingAssignment = store.subscriptions.proactivePricingAssignment;
    const proactivePricingError = store.subscriptions.proactivePricingAssignmentError;
    const proactivePricingIsLoading = store.subscriptions.proactivePricingAssignmentIsLoading;
    const isTrialEligible = store.subscriptions.isProactiveUpsellTrialEligible;
    const commercialPlanSnapshot = store.platformInfo.commercialPlanSnapshot;
    const commercialRolloutIsActive = isCommercialRolloutActive({
      catalog: store.platformInfo.commercialCatalog,
      snapshot: commercialPlanSnapshot,
    });
    const title = (() => {
      if (store.subscriptions.proactiveUpsellTitle) {
        return store.subscriptions.proactiveUpsellTitle;
      }

      if (store.settingsModal.memAgentSettingsEntrySource !== "editor_reminder_shill") {
        return undefined;
      }

      return isTrialEligible
        ? REMINDER_SHILL_PROACTIVE_TRIAL_TITLE
        : REMINDER_SHILL_PROACTIVE_PAYWALL_TITLE;
    })();

    useEffect(() => {
      setIsSubscribing(false);
    }, [checkoutResetKey]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      if (commercialRolloutIsActive) {
        return;
      }

      let canceled = false;

      store.subscriptions
        .fetchProactivePricingAssignment()
        .catch(() => undefined)
        .finally(() => {
          if (!canceled) {
            trackEvent(TrackedEvent.PaywallView, buildProactivePaywallEventPayload({ store }));
          }
        });

      return () => {
        canceled = true;
      };
    }, [commercialRolloutIsActive, isOpen, store]);

    const retryPricingAssignment = async () => {
      try {
        await store.subscriptions.fetchProactivePricingAssignment();
      } catch {
        // The visible error state is owned by the subscription store.
      }
    };

    const handleSubscribe = async () => {
      if (checkoutIsPending) {
        return;
      }

      trackEvent(TrackedEvent.PaywallUpgradeClick, buildProactivePaywallEventPayload({ store }));
      setIsSubscribing(true);
      let didStartExternalNavigation = false;

      try {
        const checkoutSession = await startProactiveCheckout({
          location,
          reminderShillUpsellAttributionContext:
            store.settingsModal.reminderShillUpsellAttributionContext,
          store,
        });

        didStartExternalNavigation = checkoutSession.didStartExternalNavigation;
        if (!didStartExternalNavigation) {
          onClose();
        }
      } catch (error) {
        trackEvent(TrackedEvent.PaywallUpgradeFailed, {
          ...buildProactivePaywallEventPayload({ store }),
          failure_stage: "checkout_start",
          failure_reason: "checkout_start_error",
          error_message: error instanceof Error ? error.message : "Unknown checkout error",
        });
        logger.error({
          message: "[ProactiveUpsellModal] Failed to start proactive checkout",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
        toastModule.triggerToast({
          content:
            error instanceof Error
              ? error.message
              : "Failed to start Proactive checkout. Please try again.",
        });
      } finally {
        if (!didStartExternalNavigation) {
          setIsSubscribing(false);
        }
      }
    };

    if (commercialRolloutIsActive) {
      return (
        <UpsellModal
          isOpen={isOpen}
          onClose={onClose}
          titleOverride={title ?? "Choose your Mem plan"}
        />
      );
    }

    return (
      <ProactiveUpsellModalView
        isOpen={isOpen}
        isTrialEligible={isTrialEligible}
        title={title}
        onClose={onClose}
        onSubscribe={handleSubscribe}
        onRetryPricing={retryPricingAssignment}
        isSubscribing={checkoutIsPending}
        proactivePrice={proactivePricingAssignment?.display_price}
        pricingIsLoading={
          proactivePricingIsLoading ||
          (!proactivePricingAssignment && proactivePricingError === undefined)
        }
        proactivePriceCents={proactivePricingAssignment?.display_price_cents}
        pricingError={proactivePricingError}
      />
    );
  }
);
